import { InvoiceImportErrorCode } from '../Types/invoiceImport.types';

/**
 * Polish user-friendly error messages for InvoiceImportErrorCode
 * Maps backend error codes to frontend display messages
 */
export const ERROR_MESSAGES: Record<InvoiceImportErrorCode, string> = {
  [InvoiceImportErrorCode.Unknown]:
    'Wystąpił nieznany błąd. Spróbuj ponownie lub skontaktuj się z pomocą techniczną.',

  [InvoiceImportErrorCode.UnsupportedFormat]:
    'Format pliku nie jest obsługiwany. Dozwolone formaty: JPEG, PNG, TIFF, PDF, XML (KSeF), CSV, JSON.',

  [InvoiceImportErrorCode.InvalidFileType]:
    'Nieprawidłowy typ pliku. Sprawdź, czy plik nie jest uszkodzony.',

  [InvoiceImportErrorCode.FileTooLarge]: 'Plik jest za duży. Maksymalny rozmiar to 10 MB.',

  [InvoiceImportErrorCode.InvalidImageResolution]:
    'Rozdzielczość obrazu jest za niska. Minimalne wymagania: 150 DPI, 1000x1000 pikseli. Prześlij lepszej jakości zdjęcie.',

  [InvoiceImportErrorCode.ParsingError]:
    'Nie udało się odczytać pliku. Sprawdź poprawność formatu (struktura XML/CSV/JSON może być nieprawidłowa).',

  [InvoiceImportErrorCode.MissingMandatoryField]:
    'Brakuje wymaganych pól w fakturze (np. numer faktury, data wystawienia, kwota brutto).',

  [InvoiceImportErrorCode.LowOcrConfidence]:
    'Jakość rozpoznania tekstu (OCR) jest za niska. Prześlij lepszej jakości zdjęcie lub skan z wyższą rozdzielczością.',

  [InvoiceImportErrorCode.DuplicateInvoiceNumber]: 'Faktura o tym numerze już istnieje w systemie.',

  [InvoiceImportErrorCode.DuplicateFileHash]: 'Ten plik został już wcześniej zaimportowany.',

  [InvoiceImportErrorCode.ExternalServiceError]:
    'Błąd usługi zewnętrznej (Azure Cloud). Spróbuj ponownie za chwilę.',

  [InvoiceImportErrorCode.Timeout]:
    'Przekroczono limit czasu przetwarzania (30 sekund). Prześlij mniejszy plik lub spróbuj ponownie.',

  [InvoiceImportErrorCode.NetworkError]:
    'Błąd połączenia sieciowego. Sprawdź połączenie z internetem i spróbuj ponownie.',

  [InvoiceImportErrorCode.ImporterNotFound]:
    'Nie znaleziono odpowiedniego importera dla tego typu pliku. Sprawdź format pliku.',

  [InvoiceImportErrorCode.ValidationError]:
    'Błąd walidacji danych. Sprawdź poprawność danych w fakturze.',
};

/**
 * Gets user-friendly error message for given error code
 *
 * @param errorCode - Backend error code
 * @returns Polish error message
 */
export function getErrorMessage(errorCode: InvoiceImportErrorCode | null | undefined): string {
  if (!errorCode) {
    return ERROR_MESSAGES[InvoiceImportErrorCode.Unknown];
  }

  return ERROR_MESSAGES[errorCode] || ERROR_MESSAGES[InvoiceImportErrorCode.Unknown];
}

/**
 * Checks if error code represents a duplicate
 *
 * @param errorCode - Backend error code
 * @returns True if error is duplicate-related
 */
export function isDuplicateError(errorCode: InvoiceImportErrorCode | null | undefined): boolean {
  return (
    errorCode === InvoiceImportErrorCode.DuplicateInvoiceNumber ||
    errorCode === InvoiceImportErrorCode.DuplicateFileHash
  );
}

/**
 * Checks if error is recoverable (user can retry)
 *
 * @param errorCode - Backend error code
 * @returns True if user should be allowed to retry
 */
export function isRecoverableError(errorCode: InvoiceImportErrorCode | null | undefined): boolean {
  if (!errorCode) return true;

  // Non-recoverable errors (duplicates, validation)
  const nonRecoverable = [
    InvoiceImportErrorCode.DuplicateInvoiceNumber,
    InvoiceImportErrorCode.DuplicateFileHash,
    InvoiceImportErrorCode.UnsupportedFormat,
    InvoiceImportErrorCode.InvalidFileType,
    InvoiceImportErrorCode.FileTooLarge,
  ];

  return !nonRecoverable.includes(errorCode);
}

/**
 * Gets MUI Alert severity based on error code
 *
 * @param errorCode - Backend error code
 * @returns MUI Alert severity
 */
export function getErrorSeverity(
  errorCode: InvoiceImportErrorCode | null | undefined
): 'error' | 'warning' | 'info' {
  if (!errorCode) return 'error';

  // Warnings (user can take action)
  if (
    errorCode === InvoiceImportErrorCode.LowOcrConfidence ||
    errorCode === InvoiceImportErrorCode.InvalidImageResolution
  ) {
    return 'warning';
  }

  // Info (duplicates - not really errors)
  if (isDuplicateError(errorCode)) {
    return 'info';
  }

  // Errors (everything else)
  return 'error';
}
